import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyLanguagePreference,
  formatLocaleDateTime,
  formatLocaleNumber,
  readLanguagePreference,
  resolveLocale,
  translateLocale,
  type AppLocale,
  type LanguagePreference,
  type Translate,
} from "./i18n-core";

export {
  applyLanguagePreference,
  currentLocale,
  detectSystemLocale,
  formatLocaleDateTime,
  formatLocaleNumber,
  languageStorageKey,
  localizeError,
  readLanguagePreference,
  resolveLocale,
  supportedLanguages,
  translateCurrent,
  translateLocale,
  type AppLocale,
  type LanguagePreference,
  type Translate,
} from "./i18n-core";

interface I18nContextValue {
  locale: AppLocale;
  preference: LanguagePreference;
  setPreference: (preference: LanguagePreference) => void;
  t: Translate;
  formatDateTime: (value: string | number | Date) => string;
  formatNumber: (value: number) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<LanguagePreference>(
    readLanguagePreference,
  );
  const [systemRevision, setSystemRevision] = useState(0);
  const locale = useMemo(
    () => resolveLocale(preference),
    [preference, systemRevision],
  );
  useEffect(() => {
    applyLanguagePreference(preference);
    document.title = translateLocale(locale, "app.documentTitle");
  }, [preference, locale]);
  useEffect(() => {
    if (preference !== "system") return;
    const refresh = () => setSystemRevision((value) => value + 1);
    window.addEventListener("languagechange", refresh);
    return () => window.removeEventListener("languagechange", refresh);
  }, [preference]);
  const setPreference = useCallback((value: LanguagePreference) => {
    applyLanguagePreference(value);
    setPreferenceState(value);
  }, []);
  const t = useCallback<Translate>(
    (key, variables) => {
      return translateLocale(locale, key, variables);
    },
    [locale],
  );
  const formatDateTime = useCallback(
    (value: string | number | Date) => formatLocaleDateTime(locale, value),
    [locale],
  );
  const formatNumber = useCallback(
    (value: number) => formatLocaleNumber(locale, value),
    [locale],
  );
  const value = useMemo(
    () => ({ locale, preference, setPreference, t, formatDateTime, formatNumber }),
    [formatDateTime, formatNumber, locale, preference, setPreference, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
